import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useGameSocket } from '../hooks/useGameSocket';
import './Party.css';

interface TriviaQuestion {
  id: string;
  question: string;
  answer: string;
  explainer?: string;
}

interface TriviaMode {
  currentQuestion: TriviaQuestion | null;
  showAnswer: boolean;
  controllerCount: number;
  roundIndex: number;
  roundSize: number;
}

interface TriviaSettings {
  autoAdvance: boolean;
  questionTimeMs: number;
  answerTimeMs: number;
}

export default function Party() {
  useEffect(() => { document.title = 'Gamy Trivia'; }, []);
  const [triviaMode, setTriviaMode] = useState<TriviaMode>({
    currentQuestion: null,
    showAnswer: false,
    controllerCount: 0,
    roundIndex: 0,
    roundSize: 0,
  });
  const [settings, setSettings] = useState<TriviaSettings>({
    autoAdvance: false,
    questionTimeMs: 8000,
    answerTimeMs: 5000,
  });
  const [totalQuestions, setTotalQuestions] = useState(0);

  const { send } = useGameSocket((msg) => {
    if (msg.type !== 'STATE') return;
    if (msg.triviaMode) setTriviaMode(msg.triviaMode);
    if (msg.triviaSettings) setSettings(msg.triviaSettings);
    if (Array.isArray(msg.triviaQuestions)) setTotalQuestions(msg.triviaQuestions.length);
  });

  function reveal() { send({ type: 'TRIVIA_REVEAL' }); }
  function next() { send({ type: 'TRIVIA_NEXT' }); }
  function toggleAuto() {
    send({ type: 'TRIVIA_SET_SETTINGS', settings: { autoAdvance: !settings.autoAdvance } });
  }
  function setQuestionTime(ms: number) {
    send({ type: 'TRIVIA_SET_SETTINGS', settings: { questionTimeMs: ms } });
  }
  function setAnswerTime(ms: number) {
    send({ type: 'TRIVIA_SET_SETTINGS', settings: { answerTimeMs: ms } });
  }

  const controllerUrl = `${window.location.origin}/party/control`;
  const q = triviaMode.currentQuestion;

  return (
    <div className="party-screen">
      <div className="party-header">
        <div className="party-title">Gamy Trivia</div>
        <div className="party-meta">
          {triviaMode.roundSize > 0 && (
            <span>Q {triviaMode.roundIndex + 1} of {triviaMode.roundSize} · </span>
          )}
          {triviaMode.controllerCount} {triviaMode.controllerCount === 1 ? 'controller' : 'controllers'}
          {settings.autoAdvance && <span className="party-auto-badge"> · AUTO</span>}
        </div>
      </div>

      <div className="party-body">
        {q ? (
          <>
            <div className="party-question">{q.question}</div>
            {triviaMode.showAnswer && (
              <div className="party-answer">
                <div className="party-answer-label">ANSWER</div>
                <div className="party-answer-text">{q.answer}</div>
                {q.explainer && (
                  <div className="party-explainer">{q.explainer}</div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="party-empty">No questions in the database yet. Add some on the host panel.</div>
        )}
      </div>

      <div className="party-footer">
        <div className="party-controls">
          <button
            className="party-btn party-btn-reveal"
            onClick={reveal}
            disabled={!q || triviaMode.showAnswer}
          >
            {triviaMode.showAnswer ? 'Revealed' : 'Reveal Answer'}
          </button>
          <button
            className="party-btn party-btn-next"
            onClick={next}
            disabled={!q}
          >
            Next →
          </button>
          <button
            className={`party-btn party-btn-toggle ${settings.autoAdvance ? 'party-btn-toggle-on' : ''}`}
            onClick={toggleAuto}
          >
            Auto: {settings.autoAdvance ? 'ON' : 'OFF'}
          </button>

          <div className={`party-timing ${!settings.autoAdvance ? 'party-timing-dim' : ''}`}>
            <label className="party-timing-row">
              <span className="party-timing-label">Q:</span>
              <input
                type="range"
                min={2000}
                max={30000}
                step={500}
                value={settings.questionTimeMs}
                onChange={e => setQuestionTime(Number(e.target.value))}
                disabled={!settings.autoAdvance}
              />
              <span className="party-timing-value">{(settings.questionTimeMs / 1000).toFixed(1)}s</span>
            </label>
            <label className="party-timing-row">
              <span className="party-timing-label">A:</span>
              <input
                type="range"
                min={2000}
                max={30000}
                step={500}
                value={settings.answerTimeMs}
                onChange={e => setAnswerTime(Number(e.target.value))}
                disabled={!settings.autoAdvance}
              />
              <span className="party-timing-value">{(settings.answerTimeMs / 1000).toFixed(1)}s</span>
            </label>
          </div>
        </div>

        <div className="party-qr-strip">
          <div className="party-qr-mini">
            <QRCodeSVG value={controllerUrl} size={96} bgColor="#ffffff" fgColor="#000000" />
          </div>
          <div className="party-qr-strip-text">
            <div className="party-qr-strip-label">Phone control</div>
            <div className="party-qr-strip-url">{controllerUrl.replace(/^https?:\/\//, '')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
